import { memo, useCallback, useEffect, useMemo } from 'react';
import { cn, Price, YandexShare } from '@divlab/divanui';
import {
  IconBox,
  IconCancel,
  IconCar,
  IconMap,
  IconChevronRight,
  IconEmptyMessage,
  IconShare,
} from '@divlab/divanui/icons';

import * as ConfiguratorQuery from '@Queries/Configurator';
import * as DeliveryTerms from '@Queries/DeliveryTerms';
import * as ProductSellPoints from '@Queries/ProductSellPoints';
import * as Profile from '@Queries/Profile';
import * as PageProductStore from '@Stores/PageProduct';
import * as ModalWindows from '@Stores/ModalWindows';
import { useRequest } from '@Contexts/Request';
import { useDeps } from '@Contexts/DI';
import { useMeta } from '@Queries/Meta';
import { useCartShortInfo } from '@Queries/Cart';
import { useFeatureFlags } from '@Contexts/FeatureFlags';
import { useRelatedProducts } from '@Queries/RelatedProducts';
import useTranslation from '@Queries/useTranslation';
import rearrangeCategories from './elements/rearrangeCategories';
import generateProductHash from '@Utils/generateProductHash';
import useNavigation from '@Navigation/useNavigation';
import useMedias from '@Hooks/useMedias';
import Button from '@UI/Button';
import Link from '@UI/Link';
import OnSaleSoon from '@Pages/PageProduct/elements/OnSaleSoon';
import OnSaleSoonTab from '@Components/OnSaleSoonTab';
import AboutReceiptButton from '@Components/AboutReceiptButton';
import YandexSplitBadge from '@Components/YandexSplitBadge';
import AsyncOrderBonuses from '@Components/AsyncOrderBonuses';
import YandexSplitWidget from '@Components/YandexSplitWidget';
import FullСashbackTag from '@Components/FullCashbackTag';
import LinksList from '../LinksList';
import BuyButton from '../BuyButton';
import OutOfStock from '../OutOfStock';
import ProductLike from '../ProductLike';
import RelatedProducts from './elements/RelatedProducts';
import DefectDescription from './elements/DefectDescription';
import CreditTabWrapper from './elements/CreditTabWrapper';
import CheaperProduct from './elements/CheaperProduct';
import Configurator from './elements/Configurator';
import Tag from './elements/Tag';
import PromoCodePrices from './elements/PromoCodePrices';
import DefaultPrices from './elements/DefaultPrices';
import styles from './Sidebar.module.css';

import type { FC, HTMLAttributes, MouseEvent, PropsWithChildren } from 'react';
import type { PecomDeliveryParameters } from '@Components/PecomDelivery';
import type { ProductTagData } from '@Types/Product';
import type { RelatedProductData } from '@Modals/RelatedProductsEasyBuyModal';
import type { ConfiguratorData } from '../..';

export interface SidebarProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  goodsInfo?: PecomDeliveryParameters[];
  configurator: ConfiguratorData;
  visibilityTrackingRef?: (node?: Element | null) => void;
  onClickReviews?: (e: MouseEvent) => void;
  onClickAtr?: (e: MouseEvent) => void;
}

export interface ContainerProps extends PropsWithChildren {
  className?: string;
  scrollable?: boolean;
  cnLayer?: string;
}
// Скрываем пункт для проработки
const pickup = null;

const Sidebar: FC<SidebarProps> = (props) => {
  const {
    className,
    configurator,
    onClickReviews,
    visibilityTrackingRef,
    onClickAtr,
    goodsInfo,
    ...restProps
  } = props;
  const page = PageProductStore.useData();
  const { isMobile, isMobileM } = useMedias();
  const { t } = useTranslation();
  const { pathname } = useNavigation();
  const { data: meta } = useMeta();
  const { region } = meta;
  const isAtr = page.isAtr;
  const { personalAreaAvailable } = useFeatureFlags();
  const { isRus } = useRequest();
  const deliveryPrice = parseInt(page.deliveryPage.price) || null;
  const profile = Profile.useProfile();
  const isClientDesigner = profile.data?.clientType === 'designer';
  const promocodeCheck = isRus ? !page.promoCode : true;
  const isExtraBonus = page.product.extraBonus;
  const isBonusesShown = page.isAvailable && !isClientDesigner && personalAreaAvailable;
  const { analytics } = useDeps();
  const textOutOfStock = t('unavailable');
  const entityId = 'configurator-textile';
  const { price, id, isBasic, constructorHash, parameterValues } = configurator.configuration;
  const { data: shortCard } = useCartShortInfo();

  const { data: relatedProducts } = useRelatedProducts(page.product.id);
  const deliveryTerms = DeliveryTerms.useDeliveryTerms({
    productId: id || page.product.id,
  });
  const isComingSoon = page.product.status === 'ComingSoon';
  const showrooms = ProductSellPoints.useProductSellPoints({
    productId: page.product.id,
  });
  const { resetFilters } = ConfiguratorQuery.useResetFilters(id);

  const previewRelatedProducts = useMemo(() => {
    const hashes = shortCard?.positions || [];
    const products = relatedProducts?.products || [];

    // Получаем добавленные в корзину товары и сортируем их
    const addedProducts = products
      .filter((item) => hashes.includes(generateProductHash({ shopProductId: item.id })))
      .sort(
        (a, b) =>
          hashes.indexOf(generateProductHash({ shopProductId: b.id })) -
          hashes.indexOf(generateProductHash({ shopProductId: a.id })),
      );

    // Группируем оставшиеся товары по typeId
    const grouped = products.reduce((acc, item) => {
      const hash = generateProductHash({ shopProductId: item.id });
      if (!hashes.includes(hash)) {
        acc[item.typeId] = acc[item.typeId] || [];
        acc[item.typeId].push(item);
      }
      return acc;
    }, {} as Record<number, RelatedProductData[]>);

    // Подсчитываем добавленные товары по typeId
    const countByTypeId = addedProducts.reduce((acc, item) => {
      acc[item.typeId] = (acc[item.typeId] || 0) + 1;
      return acc;
    }, {} as Record<number, number>);

    const unaddedProductsGroup = Object.entries(grouped)
      .map(([typeId, group]) => {
        const addedCount = countByTypeId[Number(typeId)] || 0;
        if (addedCount < 3) {
          return [...group.slice(0, 3 - addedCount)];
        }
      })
      .filter(Boolean);

    const unaddedProducts = rearrangeCategories(unaddedProductsGroup);

    return addedProducts.concat(...unaddedProducts);
  }, [relatedProducts?.products, shortCard?.positions]);

  const tags = useMemo<ProductTagData[]>(() => {
    if (isBasic) {
      return page.product.tags || [];
    }

    const fabricTags =
      configurator.parameterValues.find((val) => val.selected && val.type === 'fabric')?.tags
        .typeIds || [];

    return fabricTags.map((tag) => configurator.types[tag]);
  }, [configurator.parameterValues, configurator.types, isBasic, page.product.tags]);

  const deliveryDate = useMemo(() => {
    if (!deliveryTerms.data) return null;

    return deliveryTerms.data[0];
  }, [deliveryTerms.data]);

  const showDeliveryDate = deliveryDate;

  const mainCredit = useMemo(() => {
    if (!configurator.configuration.credit) return null;
    const availableCredit = configurator.configuration.credit.find((credit) => credit.isMain);

    if (availableCredit) {
      return availableCredit;
    }

    return configurator.configuration.credit.find((credit) => credit.creditAvailable);
  }, [configurator.configuration]);

  const countReviewsText = useMemo(() => {
    const reviews = (page.reviewsSubgallery || []).filter((review) => {
      return review.source === 'site';
    });

    const label = t('plural-reviews', reviews.length);

    return `${reviews.length} ${label}`;
  }, [page, t]);

  const handleClickShowroom = useCallback(() => {
    ModalWindows.open('Showrooms', { showrooms: showrooms.sellPoints, fullscreen: isMobile });
  }, [showrooms?.sellPoints, isMobile]);

  const handleClickFinalPrice = useCallback(() => {
    ModalWindows.open('FinalPrice', { fullscreen: isMobile });
  }, [isMobile]);

  const handleClickPickup = useCallback(() => {
    ModalWindows.open('Pickup', { productId: page.product.id });
  }, [page.product.id]);

  const handleClickWarranty = useCallback(async () => {
    ModalWindows.open('WarrantyProducts', {
      shopProductId: page.product.id,
      warranty: page.warranty,
    });
  }, [page.warranty, page.product.id]);

  const handleClickDeliveryInformation = useCallback(() => {
    analytics.dispatchEvent('delivery.click', { url: pathname });

    ModalWindows.open('DeliveryInformation', {
      delivery: page.deliveryPage,
      goodsInfo,
      deliveryDate: showDeliveryDate && deliveryDate,
      fullscreen: isMobile,
    });
  }, [page.deliveryPage, goodsInfo, deliveryDate, showDeliveryDate, analytics, pathname, isMobile]);

  const handleClickNotifyAboutReceipt = useCallback(() => {
    ModalWindows.open('NotifyAboutReceipt', {
      product: page.product,
      group: page.modules.length !== 0,
    });
  }, [page.modules, page.product]);

  const handleClickReviews = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();

      if (onClickReviews) onClickReviews(e);
    },
    [onClickReviews],
  );

  const handleClickAtr = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();

      if (onClickAtr) onClickAtr(e);
    },
    [onClickAtr],
  );

  useEffect(() => {
    return () => {
      resetFilters();
    };
  }, [resetFilters]);

  return (
    <div {...restProps} className={cn(styles.sidebar, className)}>
      <div className={styles.shortName}>
        <div className={styles.modelName}>{page.product.model}</div>
        <div className={styles.icons}>
          {page.reviewsPhotoCount > 0 && (
            <div className={styles.wrapperRating} onClick={handleClickReviews}>
              <IconEmptyMessage className={styles.iconMessage} />

              <span className={styles.countReviews} data-testid='sidebar-feedback'>
                {countReviewsText}
              </span>
            </div>
          )}
          {!isMobileM && (
            <Link className={styles.wrapperAction}>
              <YandexShare
                services={['telegram', 'whatsapp', 'vkontakte']}
                slotTrigger={<IconShare className={styles.iconAction} />}
              />
            </Link>
          )}
        </div>
      </div>
      <div className={styles.fullNameWrapper}>
        <h1 className={styles.fullName} itemProp='name'>
          {`${page.product.type} ${configurator.configuration.name}`}
        </h1>
        {page.reviewsPhotoCount > 0 && (
          <div className={styles.wrapperRating} onClick={handleClickReviews}>
            <IconEmptyMessage className={styles.iconMessage} />

            <span className={styles.countReviews} data-testid='sidebar-feedback'>
              {countReviewsText}
            </span>
          </div>
        )}
      </div>

      {isComingSoon ? (
        <div className={styles.soonWrapper}>
          <OnSaleSoonTab showIcon />

          <DefaultPrices price={price} className={styles.price} />

          <OnSaleSoon className={styles.soonBlock}>
            {!isMobileM && (
              <div className={styles.buttonsWrapper}>
                <AboutReceiptButton className={styles.button} product={page.product} />
                <ProductLike
                  className={styles.like}
                  product={{
                    ...page.product,
                    id: id,
                    parameterValues: parameterValues as any,
                  }}
                  hash={constructorHash}
                  label='Карточка товара'
                  data-testid='favorites-card'
                />
              </div>
            )}
          </OnSaleSoon>
        </div>
      ) : (
        <>
          {(tags.length > 0 || !!page.product.extraDiscount) && (
            <div className={styles.tags}>
              {page.product.extraDiscount && (
                <Tag view='extra' tag={{ title: t('tags.extra'), location: 'up', image: null }} />
              )}
              {tags.map((tag) => (
                <Tag key={tag.title} tag={tag} />
              ))}
            </div>
          )}

          {isBonusesShown && (
            <div className={styles.bonusesWrapper}>
              {!isExtraBonus && (
                <AsyncOrderBonuses productIds={[id || page.product.id]} isConstructor />
              )}
              {isExtraBonus && <FullСashbackTag />}
              {isRus && !region.isFranchise && (
                <YandexSplitBadge
                  className={styles.splitBadge}
                  size={isMobileM ? 's' : 'm'}
                  amount={price && String(price.actual)}
                />
              )}
            </div>
          )}

          {page.isAvailable ? (
            <>
              {/* Добавляем id блоку с ценой для прокрутки к нему по клику на ссылку из закрепа */}
              <div className={styles.wrapperPrice} id='priceBlock'>
                {page.promoCode ? (
                  <PromoCodePrices className={styles.promoCodePrices} />
                ) : (
                  <DefaultPrices price={price} />
                )}
                {!!mainCredit && promocodeCheck && (
                  <CreditTabWrapper
                    className={styles.creditTab}
                    configurator={configurator}
                    product={page.product}
                    pageCredit={mainCredit}
                    data-test-id='installment-cart'
                  />
                )}
              </div>
            </>
          ) : (
            <div className={styles.wrapperOutOfStock}>
              <OutOfStock text={textOutOfStock} />
            </div>
          )}

          {page.product.cheaperProduct && (
            <CheaperProduct
              product={page.product.cheaperProduct}
              className={styles.cheaperProduct}
            />
          )}

          {isAtr && (
            <div className={styles.promoInfo} onClick={handleClickAtr}>
              {t('atr.promo-info.1')}&nbsp;{t('atr.promo-info.2')}
              <span className={styles.promoInfoBold}>{t('atr.promo-info.atr')}</span>
              <Link className={styles.more} view='secondary'>
                {t('ui.more')}
                <IconChevronRight className={styles.moreIcon} />
              </Link>
            </div>
          )}

          <div className={styles.actions}>
            {page.isAvailable ? (
              <>
                <div className={styles.buyButtonWrapper}>
                  <div className={styles.buyButtonContainer} ref={visibilityTrackingRef}>
                    <BuyButton className={styles.buyButton} configurator={configurator} />
                  </div>

                  <ProductLike
                    className={styles.like}
                    product={{
                      ...page.product,
                      id: id,
                      parameterValues: parameterValues as any,
                    }}
                    hash={constructorHash}
                    label='Карточка товара'
                    data-testid='favorites-card'
                  />
                </div>

                {page.isConfigurator && configurator && (
                  <Configurator
                    configurator={configurator}
                    entityId={entityId}
                    className={styles.configurator}
                    needVideoConsultation={page.needVideoConsultation}
                  />
                )}

                {page.isDefect && (
                  <DefectDescription className={styles.defect} text={t('has-defects')} />
                )}

                {previewRelatedProducts?.length > 0 && (
                  <RelatedProducts
                    className={styles.relatedProducts}
                    relatedProducts={previewRelatedProducts}
                    mainId={page.product.id}
                  />
                )}
              </>
            ) : (
              <Button
                className={styles.action}
                wide
                theme='secondary'
                onClick={handleClickNotifyAboutReceipt}
              >
                {t('ui.notify-about-receipt')}
              </Button>
            )}
          </div>

          <div className={styles.linksList}>
            {/*TODO: подставить данные с backend*/}
            <LinksList
              items={[
                (page.deliveryPage?.tables ||
                  page.deliveryPage?.deliveryTypes ||
                  page.deliveryPage?.delivery ||
                  page.deliveryPage?.teaser) &&
                  !page.lorenaKimbolBanner && {
                    testid: 'link-to-delivery-info',
                    icon: <IconCar className={styles.icon} />,
                    hasArrow: true,
                    label: page.deliveryPage.title,
                    addingText: deliveryPrice && !region.isFranchise && (
                      <div>
                        {t('deliver')} {showDeliveryDate && deliveryDate} {t('for')}{' '}
                        <Price price={deliveryPrice} />
                      </div>
                    ),
                    onClick: handleClickDeliveryInformation,
                  },
                pickup && {
                  icon: <IconBox className={styles.icon} />,
                  hasArrow: true,
                  label: t('links.pickup'),
                  onClick: handleClickPickup,
                  testid: 'pickup',
                },
                showrooms?.sellPoints?.length > 0 && {
                  icon: <IconMap className={styles.icon} />,
                  label: t('links.where-to-watch'),
                  hasArrow: true,
                  addingText: (
                    <div>
                      {t('is-in')} {showrooms.sellPoints.length}{' '}
                      {t('ui.plural-showrooms', showrooms.sellPoints.length)}
                    </div>
                  ),
                  onClick: handleClickShowroom,
                  testid: 'open-showrooms-modal',
                },
                price?.isFinal && {
                  icon: <IconCancel className={styles.icon} />,
                  hasArrow: true,
                  label: t('links.price-final'),
                  onClick: handleClickFinalPrice,
                  testid: 'final-price',
                },
                page.warranty && {
                  icon: <div className={cn(styles.icon, styles.warranty)} />,
                  hasArrow: true,
                  label: page.warranty.sideBarTitle,
                  onClick: handleClickWarranty,
                },
              ].filter(Boolean)}
            />
          </div>

          {isRus && mainCredit?.yandexSplitAvailable && mainCredit?.promoTag && (
            <YandexSplitWidget
              className={styles.yandexWidget}
              type='splitInfo'
              totalAmount={String(page.product.price.actual)}
            />
          )}
        </>
      )}
    </div>
  );
};

export default memo(Sidebar);
